// eslint-disable-next-line import/order
import { sentryCreateBrowserRouter } from './instrument';
import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { RouterProvider } from 'react-router-dom';
import { StyledEngineProvider } from '@mui/material/styles';
import { Global } from '@emotion/react';
import { Helmet } from 'react-helmet';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import UiThemeProvider from 'themes/UiThemeProvider';
import { store, persistor } from 'store';
import { getEnv, googleTagManager } from 'utils';
import { RootErrorBoundary } from 'routes';
import useOnlineStatus from 'hooks/useOnlineStatus';
import useServiceWorkerUpdate from 'hooks/useServiceWorkerUpdate';
import ServiceWorkerUpdateModal from 'components/service-worker-update/ServiceWorkerUpdateModal';
import { SERVICE_WORKER_ENABLED } from './serviceWorkerRegistration';
import { Provider as ContextProvider } from './context';
import Root from './Root';
import { globalStyles } from './App.styles';
import 'react-toastify/dist/ReactToastify.css';

const router = sentryCreateBrowserRouter([
  { path: '*', Component: Root, errorElement: <RootErrorBoundary /> },
]);

function AppBootstrap({ children }) {
  useOnlineStatus();
  const {
    isUpdateModalOpen,
    updateVersionNumber,
    closeUpdateModal,
    applyUpdate,
  } = useServiceWorkerUpdate();

  return (
    <>
      {children}
      {SERVICE_WORKER_ENABLED ? (
        <ServiceWorkerUpdateModal
          open={isUpdateModalOpen}
          versionNumber={updateVersionNumber}
          onClose={closeUpdateModal}
          onReload={applyUpdate}
        />
      ) : null}
    </>
  );
}

AppBootstrap.propTypes = {
  children: PropTypes.node,
};

function App() {
  useEffect(() => {
    if (getEnv('REACT_APP_GTM_ID')) {
      const gtm = googleTagManager();
      gtm.init();
    }
  }, []);

  const appName = getEnv('REACT_APP_NAME');

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ContextProvider>
          <StyledEngineProvider injectFirst>
            <UiThemeProvider>
              <Global styles={globalStyles} />
              <div className={classNames('app', appName)}>
                <Helmet>
                  <title>{getEnv('REACT_APP_TITLE')}</title>
                  <meta
                    name='description'
                    content={getEnv('REACT_APP_DESCRIPTION')}
                  />
                  <link
                    rel='icon'
                    href={`${process.env.PUBLIC_URL}/${appName}/favicon.ico`}
                  />
                  <link
                    rel='manifest'
                    href={`${process.env.PUBLIC_URL}/${appName}/manifest.json`}
                  />
                  <link
                    rel='apple-touch-icon'
                    href={`${process.env.PUBLIC_URL}/${appName}/apple-touch-icon.png`}
                  />
                  <link
                    rel='stylesheet'
                    href={`${process.env.PUBLIC_URL}/${appName}/theme.css`}
                  />
                </Helmet>
                <AppBootstrap>
                  <RouterProvider router={router} />
                </AppBootstrap>
              </div>
            </UiThemeProvider>
          </StyledEngineProvider>
        </ContextProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
